import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useMemo,
} from "react";

export const GAME_STATES = Object.freeze({
  ViewMode: "ViewMode",
  ViewModeElementSelected: "ViewModeElementSelected",
  ViewModeElementFocused: "ViewModeElementFocused",
  ViewModeElementInformation: "ViewModeElementInformation",
  ViewModeEnvironmentSelected: "ViewModeEnvironmentSelected",
  Loading: "Loading",
  ZoomingIn: "ZoomingIn",
  ZoomingOut: "ZoomingOut",
});

export const UNKNOWN_GAME_STATE = "Loading";

export const normalizeGameStateName = (rawGameStateName) => {
  if (typeof rawGameStateName !== "string") {
    return UNKNOWN_GAME_STATE;
  }

  const trimmedName = rawGameStateName.trim();
  if (!trimmedName) {
    return UNKNOWN_GAME_STATE;
  }

  return trimmedName;
};

const defaultContextValue = {
  currentGameState: UNKNOWN_GAME_STATE,
  previousGameState: null,
  setGameState: () => UNKNOWN_GAME_STATE,
  isCurrentGameState: () => false,
};

export const GameStateContext = createContext(defaultContextValue);

const gameStateReducer = (state, action) => {
  if (action.type !== "SET_GAME_STATE") return state;

  const nextGameState = action.nextGameState;
  if (state.currentGameState === nextGameState) {
    return state;
  }

  return {
    currentGameState: nextGameState,
    previousGameState: state.currentGameState,
  };
};

const getInitialGameState = (initialGameState) => ({
  currentGameState: normalizeGameStateName(initialGameState),
  previousGameState: null,
});

export const GameStateProvider = ({
  children,
  initialGameState = UNKNOWN_GAME_STATE,
}) => {
  const [gameState, dispatchGameState] = useReducer(
    gameStateReducer,
    initialGameState,
    getInitialGameState
  );

  const setGameState = useCallback((rawGameStateName) => {
    const nextGameState = normalizeGameStateName(rawGameStateName);
    dispatchGameState({
      type: "SET_GAME_STATE",
      nextGameState,
    });
    return nextGameState;
  }, []);

  const isCurrentGameState = useCallback(
    (candidateGameStates) => {
      if (!Array.isArray(candidateGameStates)) return false;

      return candidateGameStates.some(
        (candidateGameState) =>
          normalizeGameStateName(candidateGameState) === gameState.currentGameState
      );
    },
    [gameState.currentGameState]
  );

  const contextValue = useMemo(
    () => ({
      currentGameState: gameState.currentGameState,
      previousGameState: gameState.previousGameState,
      setGameState,
      isCurrentGameState,
    }),
    [
      gameState.currentGameState,
      gameState.previousGameState,
      setGameState,
      isCurrentGameState,
    ]
  );

  return (
    <GameStateContext.Provider value={contextValue}>
      {children}
    </GameStateContext.Provider>
  );
};

export const useGameState = () => useContext(GameStateContext);
